import type { ColDef, ProcessCellForExportParams, ProcessRowGroupForExportParams } from "ag-grid-enterprise";

// ─── Shared export formatting utilities ────────────────────────────────────
// Used by both AgGridComponent (client-side) and AgGridSsrmComponent (SSRM)
// for CSV/Excel cell value formatting during export.

/**
 * Single source of truth for the vote box "N elections" count label — an empty box
 * reads "Empty" rather than "0 elections". Shared by the vote box cell renderer
 * (on-screen Details cell) and `resolveElectionNameExportValue` below (CSV/clipboard/xlsx
 * export) so the spreadsheet never contradicts the screen.
 */
export function formatVoteBoxCountLabel(count: number): string {
  if (count === 0) return "Empty";
  return `${count} election${count === 1 ? "" : "s"}`;
}

/**
 * Formats a cell value for export.
 * - Group rows: appends the child count in parentheses.
 * - Array values: joins with a space separator.
 * - All other values: returned as-is.
 */
export function formatCellValue(params: ProcessCellForExportParams): string {
  const { value, node } = params;
  // Skip Tree Data group rows (real `data`) — only classic grouping's synthetic rows need the suffix.
  if (node?.group && !node.data) {
    if (value == null || value === "") return "";
    const count = node.allChildrenCount ?? node.childrenAfterFilter?.length ?? 0;
    return `${value} (${count})`;
  }
  if (Array.isArray(value)) {
    return (value as unknown[]).join(" ");
  }
  return value as string;
}

/**
 * Formats the group/tree column's value for export via `processRowGroupCallback`.
 *
 * Without this, AG Grid's own default for that column ignores `processCellCallback`
 * entirely and instead concatenates every ancestor's value with " -> " prefixes
 * (e.g. "-> Acme -> Acme Sub Org") — this restores a plain value instead.
 *
 * Tree Data group rows carry real row data (unlike classic row grouping's synthetic
 * aggregate rows, where `node.data` is undefined), so when it's present we read the
 * column's field directly off it. Otherwise, fall back to the classic-grouping shape
 * (`formatCellValue`'s group-row branch): the node's key with a child count suffix.
 */
export function formatGroupCellValue(params: ProcessRowGroupForExportParams): string {
  const { node, column } = params;
  const field = column?.getColDef().field;
  const data = node.data as Record<string, unknown> | undefined;

  if (data && field) {
    const value = data[field];
    return value == null ? "" : String(value);
  }

  const label = node.key ?? "";
  if (!label) return "";
  const count = node.allChildrenCount ?? node.childrenAfterFilter?.length ?? 0;
  return `${label} (${count})`;
}

/**
 * The Voter Status grid's `status_label` field is only the filter/group bucket
 * array (e.g. `['Key Surfaced', 'Not Voted']`) — a different concept from the
 * full multi-piece status text the cell actually renders. `status_plain` (built
 * server-side via PlainTextStatusPresenter — no dates, unlike `status`, which is
 * HTML-stripped but still carries the on-screen timestamp captions) always
 * carries that full text on the same row — prefer it for export. Returns
 * `undefined` for any other field so callers fall back to their normal value
 * resolution.
 */
export function resolveStatusLabelExportValue(
  field: string | null | undefined,
  data: Record<string, unknown> | undefined
): string | undefined {
  if (field !== "status_label" || !data) return undefined;
  const status = data["status_plain"];
  return typeof status === "string" ? status : undefined;
}

export function resolveRenderedTextExportValue(
  field: string | null | undefined,
  data: Record<string, unknown> | undefined
): string | undefined {
  if (!field || !data) return undefined;
  const text = data[`${field}_text`];
  return typeof text === "string" && text !== "" ? text : undefined;
}

export function resolveElectionNameExportValue(
  colDef: ColDef | null | undefined,
  data: Record<string, unknown> | undefined
): string | undefined {
  if (colDef?.cellRenderer !== "electionNameCellRenderer" || !data) return undefined;

  const name = data[String(colDef.field ?? "name")];
  if (typeof name !== "string" || name === "") return undefined;

  if (data.row_type === 1) {
    const parts = [name];
    const count = data.childCount;
    if (typeof count === "number") parts.push(formatVoteBoxCountLabel(count));
    if (data.entity_id !== undefined && data.entity_id !== null) parts.push(String(data.entity_id));
    return parts.join(" ");
  }

  const parts = [name];
  if (typeof data.type_label === "string" && data.type_label) parts.push(data.type_label);
  if (data.id !== undefined && data.id !== null) parts.push(String(data.id));
  return parts.join(" ");
}

export function resolveDisplayExportValue(
  colDef: ColDef | null | undefined,
  data: Record<string, unknown> | undefined
): string | undefined {
  return resolveStatusLabelExportValue(colDef?.field, data)
    ?? resolveElectionNameExportValue(colDef, data)
    ?? resolveRenderedTextExportValue(colDef?.field, data);
}
